"use client";

import React, { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";

type NewsItem = {
  id: string;
  title: string;
  url: string;
  summary: string;
  date_published: string;
  attachments?: { url: string }[];
};

type Props = {
  feedUrl: string;
};

const TourDeFranceCarousel: React.FC<Props> = ({ feedUrl }) => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    align: "start",
    loop: false,
    containScroll: "trimSnaps",
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await fetch(feedUrl);
        const data = await res.json();
        setNewsItems(data.items || []);
      } catch (error) {
        console.error("Failed to fetch Tour de France news:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [feedUrl]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (loading) return <div className="p-4 text-center">Chargement des actualités...</div>;

  return (
    <div className="relative px-4 py-2">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-3">
          {newsItems.map((item) => (
            <div
              key={item.id}
              className="min-w-0 shrink-0 grow-0 basis-[85%] sm:basis-[48%] md:basis-[32%] lg:basis-[24%] pl-3"
            >
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                {/* Image background */}
                {item.attachments && item.attachments[0]?.url ? (
                  <img
                    src={item.attachments[0].url}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900" />
                )}

                {/* Default gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent transition-opacity duration-300 group-hover:opacity-0" />

                {/* Hover gradient overlay – stronger to make smaller text readable */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                {/* Default text – clamped */}
                <div className="absolute inset-0 flex flex-col justify-end p-4 transition-opacity duration-300 group-hover:opacity-0">
                  <h2 className="text-white text-sm sm:text-base font-semibold leading-snug line-clamp-3 drop-shadow-md">
                    {item.title}
                  </h2>
                </div>

                {/* Hover text – full title in smaller font */}
                <div className="absolute inset-0 flex flex-col justify-end p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <h2 className="text-white text-xs sm:text-sm font-medium leading-snug drop-shadow-md">
                    {item.title}
                  </h2>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        aria-label="Précédent"
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm text-gray-700 hover:bg-white hover:text-black transition disabled:opacity-0 disabled:pointer-events-none"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        onClick={scrollNext}
        disabled={!canScrollNext}
        aria-label="Suivant"
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm text-gray-700 hover:bg-white hover:text-black transition disabled:opacity-0 disabled:pointer-events-none"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
    </div>
  );
};

export default TourDeFranceCarousel;
